import { Button, CircularProgress, Stack, Typography, Box } from "@mui/material";
import { imageURL } from "@shared/helpers/constants";
import { linko } from "@shared/linko";
import { type FC, useCallback, useContext, useState } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { RequestContext } from "../../contexts/RequestProvider";
import { AddEventImage } from "../add-event-image/AddEventImage";

interface EventImageSectionProps {
  imageId?: string;
  onImageIdChange?: (imageId: string) => void;
}

const EventImageSection: FC<EventImageSectionProps> = ({
  imageId: imageIdProp,
  onImageIdChange,
}) => {
  const request = useContext(RequestContext);
  const imageId = imageIdProp ?? request?.currentEvent.imageId ?? "";
  const setImageId = (nextId: string) => {
    if (onImageIdChange) {
      onImageIdChange(nextId);
      return;
    }
    request?.setCurrentEvent((prev) => ({ ...prev, imageId: nextId }));
  };
  const { currOrganization } = useOrganization();
  const { setSeverity, setAlertText } = useAlert();
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [isAddImage, setIsAddImage] = useState<boolean>(false);

  const handleImageUpload = useCallback(async (image: File) => {
    if (!currOrganization?.id) return;

    try {
      setImageLoading(true);

      const { data } = await linko.image.mutations.createImage(
        {
          organizationId: currOrganization?.id,
          isBackground: true,
        },
        { file: image },
      );

      setImageId(data.id);
      setIsAddImage(false);
    } catch {
      setSeverity("error");
      setAlertText("לא היה ניתן להעלות את התמונה");
    } finally {
      setImageLoading(false);
    }
  }, [currOrganization?.id, setSeverity, setAlertText, onImageIdChange]);

  return (
    <Stack
      width="100%"
      className="event-image-container"
      sx={{
        position: "relative",
        height: "10rem",
        maxHeight: "12rem",
        minHeight: "7rem",
        overflow: "hidden",
        borderRadius: 1,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AddEventImage
        setIsOpen={setIsAddImage}
        isOpen={isAddImage}
        imageId={imageId}
        onImageIdChange={
          onImageIdChange ? (id) => onImageIdChange(id ?? "") : undefined
        }
        onUploadImage={(image) => void handleImageUpload(image)}
        isUploading={imageLoading}
      />
      {imageLoading ? (
        <CircularProgress />
      ) : imageId ? (
        <>
          <Box
            component="img"
            className="event-image"
            src={imageURL(imageId)}
            alt="תמונת נושא למשימה"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />
          <Stack className="image-controllers" direction="row">
            <Button
              onClick={() => setImageId("")}
              variant="outlined"
            >
              מחיקת תמונה
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <Button
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              minHeight: "7rem",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
            color="inherit"
            onClick={() => setIsAddImage(true)}
          >
            <Stack
              sx={{ objectFit: "contain", width: "3rem", height: "3rem" }}
              component="img"
              src="/upload-image.png"
            />
            <Typography
              sx={{ textWrap: "nowrap", opacity: 0.5 }}
              fontSize="small"
            >
              בחירת תמונת נושא
            </Typography>
          </Button>
        </>
      )}
    </Stack>
  );
};

export default EventImageSection;
